import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authHeader, setAuthHeader] = useState('');
  const [error, setError] = useState('');
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState('leads');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    const header = 'Basic ' + btoa('admin:' + password);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { Authorization: header },
      });
      if (res.ok) {
        setAuthed(true);
        setAuthHeader(header);
      } else {
        setError('Incorrect password.');
      }
    } catch {
      setError('Connection error.');
    }
  };

  useEffect(() => {
    if (!authed) return;
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/api/admin/leads`, { headers: { Authorization: authHeader } }).then(r => r.json()),
      fetch(`${API_URL}/api/admin/contacts`, { headers: { Authorization: authHeader } }).then(r => r.json()),
    ]).then(([leadsData, contactsData]) => {
      setLeads(leadsData.leads || []);
      setContacts(contactsData.contacts || []);
    }).finally(() => setLoading(false));
  }, [authed, authHeader]);

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (!authed) {
    return (
      <div className="bg-[#f5f1e8] min-h-screen flex items-center justify-center px-6">
        <SEO title="Admin | Your Setl" noIndex={true} />
        <div className="max-w-sm w-full">
          <h1
            className="text-2xl font-light text-[#3a2f28] mb-8 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Admin Access
          </h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-4 py-3 bg-white border border-[#d4a574]/40 rounded text-[#3a2f28] placeholder:text-[#3a2f28]/40 focus:outline-none focus:border-[#d4a574] focus:ring-1 focus:ring-[#d4a574]"
              style={{ fontFamily: 'Inter, sans-serif' }}
              data-testid="admin-password-input"
            />
            {error && <p className="text-red-600 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{error}</p>}
            <button
              type="submit"
              className="w-full px-8 py-3 bg-[#3a2f28] text-[#f5f1e8] font-light tracking-wide hover:bg-[#d4a574] transition-all duration-300 rounded text-base"
              style={{ fontFamily: 'Inter, sans-serif' }}
              data-testid="admin-login-btn"
            >
              Log In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f5f1e8] min-h-screen pt-24 px-6 pb-12">
      <SEO title="Admin | Your Setl" noIndex={true} />
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1
            className="text-2xl md:text-3xl font-light text-[#3a2f28]"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Dashboard
          </h1>
          <button
            onClick={() => { setAuthed(false); setAuthHeader(''); setPassword(''); }}
            className="text-sm text-[#3a2f28]/60 hover:text-[#3a2f28] underline underline-offset-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
            data-testid="admin-logout-btn"
          >
            Log out
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-white rounded-lg p-5 border border-[#d4a574]/20">
            <p className="text-sm text-[#3a2f28]/60 mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>Brochure Downloads</p>
            <p className="text-3xl font-light text-[#3a2f28]" style={{ fontFamily: 'Playfair Display, serif' }}>{leads.length}</p>
          </div>
          <div className="bg-white rounded-lg p-5 border border-[#d4a574]/20">
            <p className="text-sm text-[#3a2f28]/60 mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>Contact Inquiries</p>
            <p className="text-3xl font-light text-[#3a2f28]" style={{ fontFamily: 'Playfair Display, serif' }}>{contacts.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[#3a2f28]/10 rounded-lg p-1 max-w-xs">
          <button
            onClick={() => setTab('leads')}
            className={`flex-1 px-4 py-2 text-sm rounded-md transition-all ${tab === 'leads' ? 'bg-[#3a2f28] text-[#f5f1e8]' : 'text-[#3a2f28]/70 hover:text-[#3a2f28]'}`}
            style={{ fontFamily: 'Inter, sans-serif' }}
            data-testid="tab-leads"
          >
            Brochure Leads
          </button>
          <button
            onClick={() => setTab('contacts')}
            className={`flex-1 px-4 py-2 text-sm rounded-md transition-all ${tab === 'contacts' ? 'bg-[#3a2f28] text-[#f5f1e8]' : 'text-[#3a2f28]/70 hover:text-[#3a2f28]'}`}
            style={{ fontFamily: 'Inter, sans-serif' }}
            data-testid="tab-contacts"
          >
            Contacts
          </button>
        </div>

        {loading ? (
          <p className="text-[#3a2f28]/60 text-center py-12" style={{ fontFamily: 'Inter, sans-serif' }}>Loading...</p>
        ) : tab === 'leads' ? (
          <div className="bg-white rounded-lg border border-[#d4a574]/20 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="leads-table">
                <thead>
                  <tr className="bg-[#3a2f28]/5 border-b border-[#d4a574]/20">
                    <th className="px-5 py-3 text-xs font-medium uppercase tracking-wider text-[#3a2f28]/60" style={{ fontFamily: 'Inter, sans-serif' }}>Email</th>
                    <th className="px-5 py-3 text-xs font-medium uppercase tracking-wider text-[#3a2f28]/60" style={{ fontFamily: 'Inter, sans-serif' }}>Consent</th>
                    <th className="px-5 py-3 text-xs font-medium uppercase tracking-wider text-[#3a2f28]/60" style={{ fontFamily: 'Inter, sans-serif' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, i) => (
                    <tr key={i} className="border-b border-[#d4a574]/10 last:border-0 hover:bg-[#f5f1e8]/50">
                      <td className="px-5 py-3 text-sm text-[#3a2f28]" style={{ fontFamily: 'Inter, sans-serif' }}>
                        <a href={`mailto:${lead.email}`} className="text-[#d4a574] hover:underline">{lead.email}</a>
                      </td>
                      <td className="px-5 py-3 text-sm text-[#5a7320]" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {lead.consent ? 'Yes' : 'No'}
                      </td>
                      <td className="px-5 py-3 text-sm text-[#3a2f28]/60" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {formatDate(lead.timestamp)}
                      </td>
                    </tr>
                  ))}
                  {leads.length === 0 && (
                    <tr><td colSpan={3} className="px-5 py-8 text-center text-sm text-[#3a2f28]/40" style={{ fontFamily: 'Inter, sans-serif' }}>No brochure leads yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-[#d4a574]/20 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="contacts-table">
                <thead>
                  <tr className="bg-[#3a2f28]/5 border-b border-[#d4a574]/20">
                    <th className="px-5 py-3 text-xs font-medium uppercase tracking-wider text-[#3a2f28]/60" style={{ fontFamily: 'Inter, sans-serif' }}>Name</th>
                    <th className="px-5 py-3 text-xs font-medium uppercase tracking-wider text-[#3a2f28]/60" style={{ fontFamily: 'Inter, sans-serif' }}>Email</th>
                    <th className="px-5 py-3 text-xs font-medium uppercase tracking-wider text-[#3a2f28]/60" style={{ fontFamily: 'Inter, sans-serif' }}>Phone</th>
                    <th className="px-5 py-3 text-xs font-medium uppercase tracking-wider text-[#3a2f28]/60 hidden md:table-cell" style={{ fontFamily: 'Inter, sans-serif' }}>Message</th>
                    <th className="px-5 py-3 text-xs font-medium uppercase tracking-wider text-[#3a2f28]/60" style={{ fontFamily: 'Inter, sans-serif' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c, i) => (
                    <tr key={i} className="border-b border-[#d4a574]/10 last:border-0 hover:bg-[#f5f1e8]/50">
                      <td className="px-5 py-3 text-sm text-[#3a2f28] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>{c.name}</td>
                      <td className="px-5 py-3 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                        <a href={`mailto:${c.email}`} className="text-[#d4a574] hover:underline">{c.email}</a>
                      </td>
                      <td className="px-5 py-3 text-sm text-[#3a2f28]/70" style={{ fontFamily: 'Inter, sans-serif' }}>{c.phone || '-'}</td>
                      <td className="px-5 py-3 text-sm text-[#3a2f28]/70 max-w-[200px] truncate hidden md:table-cell" style={{ fontFamily: 'Inter, sans-serif' }}>{c.message}</td>
                      <td className="px-5 py-3 text-sm text-[#3a2f28]/60" style={{ fontFamily: 'Inter, sans-serif' }}>{formatDate(c.timestamp)}</td>
                    </tr>
                  ))}
                  {contacts.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-[#3a2f28]/40" style={{ fontFamily: 'Inter, sans-serif' }}>No contact submissions yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
